import { FC, useEffect, useMemo, useState } from 'react';
import { ShepherdTour } from 'react-shepherd';
import { ICandidate, IJob, getJobCandidateInteraction } from 'wisestep-api';
import { ICandidateAggregatedData, IConversationState, IUserDTO } from 'wisestep-base';

import { useUserContext } from '../../../context';
import { useStatusActionPipelineContext } from '../../../context/StatusActionPipelineContext';
import { tourOptions, mappingSteps } from '../../../utils/TourStepsAndOptions';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { syncPostgreAndESCandidate } from '../../candidates/utils/CandidateHelper';
import JobScreenContextProvider from '../context/JobScreenContext';

import ConversationWidgetUI from './ConversationWidgetUI';
import SubmissionWidgetUI from './SubmissionWidgetUI';

interface IConversationWidget {
  candidate: ICandidate;
  aggregation: ICandidateAggregatedData | null;
  selectedJob: IJob | null;
}

const ConversationWidget: FC<IConversationWidget> = ({ candidate, aggregation, selectedJob }) => {
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const statusActionPipeline = useStatusActionPipelineContext();
  const [stateIntialized, setIsStateInitialized] = useState(false);
  const setIsAggLoaded = useConversationWidgetStore((state) => state.setIsAggLoaded);
  const isAggLoaded = useConversationWidgetStore((state) => state.isAggLoaded);
  const view = useConversationWidgetStore((state) => state.view);
  const [fetchedCandidate, setFetchedCandidate] = useState(
    aggregation?.jobInteraction ? aggregation.jobInteraction : candidate
  );
  const conversationStateValue: IConversationState = useMemo(() => {
    return {
      candidate: fetchedCandidate,
      currentUser: loggedInUser,
      candidateLocation: null,
      candidatePreferredLocations: [],
      recentJobMappings: [],
      autoSaveDisabledSettings: null,
      autoSaveAllowed: true,
      getDailyConversationCount: null,
      totalConversationPermanentCount: null,
      messageAndInteractionPipeline: statusActionPipeline,
    };
  }, [fetchedCandidate, loggedInUser, statusActionPipeline]);

  useEffect(() => {
    if (aggregation?.jobInteraction) {
      setIsStateInitialized(false);
    } else if (!aggregation?.jobInteraction) {
      setIsAggLoaded(true);
    }
  }, [aggregation, setIsAggLoaded]);

  useEffect(() => {
    (async () => {
      if (!stateIntialized && selectedJob && aggregation?.jobInteraction?.id) {
        setIsStateInitialized(true);
        const req = await getJobCandidateInteraction(aggregation?.jobInteraction.id);
        syncPostgreAndESCandidate(req.data, candidate);
        setFetchedCandidate(req.data);
        isAggLoaded === false && setIsAggLoaded(true);
      }
    })();
  }, [
    aggregation?.jobInteraction?.id,
    candidate,
    isAggLoaded,
    selectedJob,
    setIsAggLoaded,
    stateIntialized,
  ]);

  return (
    <JobScreenContextProvider>
      {view === 'submission' ? (
        <SubmissionWidgetUI
          aggregatedData={aggregation}
          state={conversationStateValue}
          selectedJob={selectedJob}
        />
      ) : (
        <ShepherdTour steps={mappingSteps} tourOptions={tourOptions}>
          <ConversationWidgetUI
            aggregatedData={aggregation}
            state={conversationStateValue}
            selectedJob={selectedJob}
          />
        </ShepherdTour>
      )}
    </JobScreenContextProvider>
  );
};

export default ConversationWidget;
